/** Executive - *where are we, and what is in the way* (ADR-0195, the first of four fixed views).
 *
 * Seven tiles, each a view over a read that already exists: the filings (`/home/summary`), the
 * scopes and the sites (`/inventory`), the months (`/inventory/monthly`, through the same
 * `MonthlyCard` Home and the Emissions screen draw), the prior period (`/inventory/trends`), the target
 * (`/plan`) and what is waiting (`/home/summary` counts). **No tile computes**: a share is the
 * server's figures divided for display, a delta is the server's own, and the headline is the
 * inventory's total, never a sum over a register.
 *
 * **What this view does not draw, on purpose.** No intensity and no biggest movers (ADR-0191 is
 * Proposed); no year-end illustration (ADR-0195 3b refuses it unless every baseline condition can be
 * checked, and the boundary-version and restatement reads it needs do not exist); no progress
 * against the target (the plan screen says why). A tile whose read is missing is not drawn.
 */

import { CaretRight } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import { useInventoryTrends } from "../../api/dashboardReads";
import { useHomeSummary } from "../../api/homeSummary";
import type { FilingRow } from "../../api/homeSummary";
import { useOrgTree } from "../../api/vendorSummary";
import { useInventory, usePlan } from "../../api/queries";
import { Donut } from "../../components/charts";
import { StatusChip } from "../../components/ui/StatusChip";
import type { ChipTone } from "../../components/ui/StatusChip";
import { shortDate, tonnes } from "../../lib/format";
import { issuedButOwing, stateSentence } from "../filingState";
import { duePhrase, filingTitle, isLive, stepLabel } from "../homeWords";
import { BandFailures } from "../bands/BandFailures";
import { priorNote } from "../bands/bandModel";
import { MonthlyCard } from "../bands/MonthlyCard";
import { useEmissionsTrend } from "../bands/useEmissionsTrend";
import { useOpenMonth } from "../bands/useOpenMonth";
import type { Screen } from "../useScreen";
import { TransitionPathway } from "../TransitionPathway";
import { carryOf, scopeMovements, scopeSplit } from "./dashboardModel";
import styles from "./Dashboards.module.css";
import { BarRows, CountText } from "./parts";
import type { BarRow } from "./parts";
import { Tile } from "./Tile";

function verdictTone(row: FilingRow): ChipTone {
  if (!row.available) return "neutral";
  // Issued and owed something again is not the finished tone (G-Wauditor-02).
  if (issuedButOwing(row.state)) return "warn";
  switch (row.verdict) {
    case "incomplete":
    case "unsupported":
      return "crit";
    case "checks_passed":
    case "approved":
    case "issued":
      return "ok";
    default:
      return "warn";
  }
}

export function ExecutiveView({ screen }: { screen: Screen }) {
  const carry = carryOf(screen);
  const summary = useHomeSummary(screen.window, screen.method);
  const inventory = useInventory(screen.window, screen.method);
  const assessedScopes = (inventory.data?.totals_by_scope ?? [])
    .filter((card) => card.assessed)
    .map((card) => card.scope as "1" | "2" | "3");
  const trend = useEmissionsTrend(screen, screen.method, assessedScopes);
  const openMonth = useOpenMonth(screen);
  const trends = useInventoryTrends(screen.window, screen.method);
  const plan = usePlan();
  const tree = useOrgTree(screen.window?.period_start, screen.window?.period_end, screen.method);

  const split = inventory.data ? scopeSplit(inventory.data.totals_by_scope, inventory.data.total_kgco2e) : undefined;

  const siteRows: BarRow[] = (() => {
    const sites = inventory.data?.by_facility ?? [];
    const largest = Math.max(0, ...sites.map((site) => Math.abs(Number(site.kgco2e))));
    return [...sites]
      .sort((a, b) => Math.abs(Number(b.kgco2e)) - Math.abs(Number(a.kgco2e)))
      .slice(0, 7)
      .map((site) => {
        // **A bar of tonnes takes its scope's colour**: the organisation tree states each site's
        // tonnes by scope, so the bar is split into them. Until it has answered (or when it failed)
        // the bar is one neutral fill, which claims nothing about the split.
        const parts = tree.data?.facilities
          .find((candidate) => candidate.facility_id === site.facility_id)
          ?.totals.by_scope.flatMap((cell, at) =>
            Number(cell.kgco2e) > 0
              ? [{ fill: (["scope1", "scope2", "scope3"] as const)[at] ?? "scope1", weight: Number(cell.kgco2e) / 1000 }]
              : [],
          );
        return { site, parts };
      })
      .map(({ site, parts }) => ({
        id: site.facility_id,
        name: site.facility_name,
        fraction: largest > 0 ? Math.abs(Number(site.kgco2e)) / largest : 0,
        figure: `${tonnes(site.kgco2e)} t`,
        fill: "ink" as const,
        ...(parts && parts.length > 0 ? { segments: parts } : {}),
        to: { to: "/emissions" as const, search: { ...carry, site: site.facility_id } },
        openLabel: `Open the figures for ${site.facility_name}: ${tonnes(site.kgco2e)} tonnes`,
      }));
  })();

  const target = plan.data?.targets[0];
  const counts = summary.data?.counts;

  return (
    <div className={styles.view} data-testid="dashboard-executive">
      <div className={styles.span7}>
        <Tile
          id="executive-filings"
          title="Where the filings stand"
          sub="Checks passed is not approval; a person decides"
          of="The filings"
          reads={[summary]}
        >
          {summary.data && summary.data.filings.length === 0 ? (
            <p className={styles.empty}>No filing is confirmed as owed for this period.</p>
          ) : (
            <ul className={styles.rows}>
              {(summary.data?.filings ?? []).map((row) => (
                <li key={row.obligation_id} data-testid="dash-filing" data-step={row.next_step}>
                  <Link
                    to="/reports"
                    search={{ ...carry, filing: row.obligation_id }}
                    className={`${styles.row} ${styles.rowLink}`}
                    aria-label={`${filingTitle(row)}, ${row.legal_entity_name}: ${stepLabel(row)}. Open the filing.`}
                  >
                    <span className={styles.name}>
                      {filingTitle(row)}
                      <span className={styles.sub}>
                        {row.legal_entity_name}
                        {isLive(row) ? ` ${duePhrase(row)}` : ` ${row.in_force_label ?? "not in force"}`}
                      </span>
                    </span>
                    <span>
                      <StatusChip
                        tone={verdictTone(row)}
                        size="sm"
                        {...(issuedButOwing(row.state) ? { title: stateSentence(row.state) } : {})}
                      >
                        {stepLabel(row)}
                      </StatusChip>
                    </span>
                    <span className={styles.figure}>
                      {row.due_date ? shortDate(row.due_date) : <span className={styles.words}>no date</span>}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Tile>
      </div>

      <div className={styles.span5}>
        <Tile
          id="executive-scopes"
          title="Emissions by scope"
          sub="Recorded, assessed amounts"
          of="The scope totals"
          reads={[inventory]}
        >
          {split ? (
            <>
              <div className={styles.split}>
                {split.totalText ? (
                  <Donut
                    slices={split.rows
                      .filter((row) => row.assessed && row.value > 0)
                      .map((row) => ({
                        key: row.scope,
                        label: `Scope ${row.scope}`,
                        value: row.value,
                        text: `${row.text ?? ""} t`,
                        ...(row.share ? { share: row.share } : {}),
                        colour: row.scope === "1" ? "scope1" : row.scope === "2" ? "scope2" : "scope3",
                        // The arc and its legend row open the figures behind the scope, as the buttons beside do.
                        receipt: `scope-${row.scope}`,
                      }))}
                    onOpenReceipt={(receipt) => {
                      screen.openBreakdown(receipt);
                    }}
                    centre={split.totalText}
                    centreLabel="tCO₂e"
                    caption="Recorded emissions by scope"
                    size={132}
                    testId="dash-scope-donut"
                  />
                ) : null}
                <ul className={styles.legend} data-testid="dash-scope-actions">
                  {split.rows.map((row) => (
                    <li key={row.scope}>
                      {row.assessed ? (
                        <button
                          type="button"
                          className={styles.legendButton}
                          onClick={() => screen.openBreakdown(`scope-${row.scope}`)}
                          aria-label={`Open the figures behind Scope ${row.scope}: ${row.text} tonnes${row.share ? `, ${row.share}` : ""}`}
                          data-testid="dash-scope-open"
                        >
                          <span
                            className={`${styles.swatch} ${
                              row.scope === "1" ? styles.swatchScope1 : row.scope === "2" ? styles.swatchScope2 : styles.swatchScope3
                            }`}
                            aria-hidden="true"
                          />
                          <span>Open the Scope {row.scope} figures</span>
                          <CaretRight size={14} aria-hidden="true" />
                        </button>
                      ) : (
                        <span className={styles.legendButton}>
                          <span className={styles.outline} aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 2 }} />
                          <span className={styles.words}>Scope {row.scope}: not assessed</span>
                          <span />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              {inventory.data?.scope3 ? <p className={styles.note}>{inventory.data.scope3.sentence}</p> : null}
              {split.totalText ? (
                <div className={styles.foot}>
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() => screen.openBreakdown("all")}
                    data-testid="dash-total-open"
                  >
                    Open every figure behind the {split.totalText} tCO₂e
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </Tile>
      </div>

      <div className={styles.span8} data-testid="dash-executive-monthly">
        <BandFailures
          reads={[
            { of: "The monthly figures", read: trend.monthly },
            { of: "The expected bills", read: trend.coverage },
          ]}
        />
        <MonthlyCard
          scopes={assessedScopes}
          bars={trend.bars}
          reads={trend.reads}
          onOpenMonth={openMonth}
          unreconciled={trend.unreconciled}
          estimates={trend.estimates}
        />
      </div>

      <div className={styles.span4}>
        <Tile
          id="executive-sites"
          title="By site"
          sub="Largest first, tonnes CO₂e"
          of="The site totals"
          reads={[inventory]}
        >
          {siteRows.length === 0 ? (
            <p className={styles.empty}>No site totals for this window.</p>
          ) : (
            <BarRows
              rows={siteRows}
              caption="Emissions by site, tonnes CO2e, largest first"
              head={["Site", "tCO2e"]}
              testId="dash-sites"
            />
          )}
        </Tile>
      </div>

      <div className={styles.span6}>
        <Tile
          id="executive-prior"
          title="Against the prior period"
          sub="Allocated totals by scope"
          of="The comparison with the prior period"
          reads={[trends]}
        >
          {trends.data ? (
            (() => {
              const movements = scopeMovements(trends.data);
              if (movements.length === 0) {
                return <p className={styles.empty}>No figures in this window or the one before it.</p>;
              }
              return (
                <>
                  <ul className={styles.rows} data-testid="dash-prior">
                    {movements.map((row) => (
                      <li key={row.scope} data-movement={row.movement}>
                        {/* **Two windows, two links** (finding 8): this row opens the register for
                            the period shown, and the "before" figure beneath opens it for the period
                            before. They were one link to the current window, so the prior figure
                            printed here sent a reader to figures that were not its own. */}
                        <Link
                          to="/emissions"
                          search={{ ...carry, scope: row.scope }}
                          className={`${styles.row} ${styles.rowLink}`}
                          aria-label={`Scope ${row.scope}: ${row.current} tonnes${row.prior ? "" : ", no figures the period before"}. Open the figures.`}
                        >
                          <span className={styles.name}>
                            Scope {row.scope}
                            {row.prior ? null : <span className={styles.sub}>no figures before</span>}
                          </span>
                          <span className={styles.pairedTrack} aria-hidden="true">
                            <span className={styles.track}>
                              <span className={`${styles.fill} ${row.scope === "1" ? styles.fillScope1 : styles.fillScope2}`} style={{ width: `${row.currentFraction * 100}%` }} />
                            </span>
                            {row.prior ? (
                              <span className={styles.track}>
                                <span className={`${styles.fill} ${styles.fillMuted}`} style={{ width: `${row.priorFraction * 100}%` }} />
                              </span>
                            ) : null}
                          </span>
                          <span className={styles.figure}>
                            {row.current} t
                            <span className={styles.sub}>
                              {row.movement === "no_prior" ? "no prior" : row.delta ? `${row.delta} t` : "unchanged"}
                            </span>
                          </span>
                        </Link>
                        {row.prior && trends.data ? (
                          <Link
                            to="/emissions"
                            search={{
                              ...carry,
                              scope: row.scope,
                              from: trends.data.prior_start,
                              to: trends.data.prior_end,
                            }}
                            className={`${styles.sub} ${styles.link}`}
                            // A pointer target of at least 24 CSS px (WCAG 2.5.8), which the sub-line's
                            // own height is not; the browser gate measures it.
                            style={{ display: "flex", alignItems: "center", minHeight: 24 }}
                            data-testid="dash-prior-open"
                            aria-label={`Scope ${row.scope}: ${row.prior} tonnes for ${shortDate(trends.data.prior_start)} to ${shortDate(trends.data.prior_end)}, the period before. Open those figures.`}
                          >
                            {row.prior} t before
                          </Link>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {/* **A change against a part-year says so** (G-Wviewer-07): a chip, so the note costs the
                      page no sentence, and the count is the server's own months. */}
                  {priorNote(trends.data) ? (
                    <StatusChip tone="warn" size="sm" testId="dash-prior-partial">
                      {priorNote(trends.data)}
                    </StatusChip>
                  ) : null}
                  <p className={styles.note}>
                    The top bar is this period, the grey bar the one before. A period before with no
                    figures is shown as none, not as a fall to nothing.
                  </p>
                </>
              );
            })()
          ) : null}
        </Tile>
      </div>

      <div className={styles.span6}>
        <Tile
          id="executive-target"
          title="The commitment"
          sub="Stated points only"
          of="The transition plan"
          reads={[plan]}
          action={
            <Link to="/transition" search={carry} className={styles.link}>
              Targets &amp; plan
            </Link>
          }
        >
          {target ? (
            <TransitionPathway target={target} modelled={false} />
          ) : (
            <p className={styles.empty}>
              No target has been committed to. A target starts on{" "}
              <Link to="/transition" search={carry} className={styles.link}>
                Targets &amp; plan
              </Link>
              .
            </p>
          )}
        </Tile>
      </div>

      <div className={styles.span12}>
        <Tile
          id="executive-waiting"
          title="Waiting on someone"
          sub="Counts, each opening its list"
          of="What is waiting"
          reads={[summary]}
          asTable
        >
          <table className={styles.counts} data-testid="dash-waiting">
            <caption className="visuallyHidden">Waiting on someone, with a count each</caption>
            <thead>
              <tr>
                <th scope="col">Waiting</th>
                <th scope="col" className={styles.figure}>
                  Count
                </th>
              </tr>
            </thead>
            <tbody>
            {[
              { id: "approvals", name: "Approvals waiting on you", value: counts?.approvals_waiting_on_me, to: "/approvals" as const },
              { id: "documents", name: "Documents to review", value: counts?.documents_to_review, to: "/evidence" as const },
              { id: "windows", name: "Months with no bill or statement", value: counts?.missing_source_windows, to: "/sources" as const, search: { tab: "coverage", state: "missing" } },
              { id: "blocking", name: "Findings holding a filing", value: counts?.blocking_findings, to: "/readiness" as const },
              { id: "items", name: "Your open items", value: counts?.my_open_items, to: "/tasks" as const },
            ].map((item) => (
              <tr key={item.id} data-mark={item.id}>
                <td>
                  <Link
                    to={item.to}
                    search={"search" in item && item.search ? { ...carry, ...item.search } : carry}
                    className={styles.rowLink}
                    aria-label={`${item.name}: ${item.value ?? "not shown to your role"}. Open the list.`}
                  >
                    <span className={styles.name}>{item.name}</span>
                  </Link>
                </td>
                <td className={styles.figure}><CountText value={item.value} /></td>
              </tr>
            ))}
            </tbody>
          </table>
        </Tile>
      </div>
    </div>
  );
}
